import FreeText from '@algbra/ui/components/FreeText/FreeText'
import { Column, Container, Row } from '@algbra/ui/components/Grid/Grid'
import Heading from '@algbra/ui/components/Heading/Heading'
import Icon from '@algbra/ui/components/Icon/Icon'
import Permalink from '@algbra/ui/components/Permalink/Permalink'
import Whitespace from '@algbra/ui/components/Whitespace/Whitespace'
import { getIconByName } from '@algbra/ui/icons'
import { resolveAsImage } from '@algbra/ui/resolvers'
import { GatsbyResolvedAsset } from '@algbra/ui/types/image'
import classNames from 'classnames'
import { GatsbyImage } from 'gatsby-plugin-image'
import { head, map } from 'rambdax'
import { useState } from 'react'
import styles from './CardSelector.module.scss'
import { ReactComponent as CurvedArrawIcon } from '../../images/curvedArrow.svg'
import { ReactComponent as RecycleIcon } from '../../images/recycleIcon.svg'

export type CardSelectorColor = {
  color: string
  image: GatsbyResolvedAsset
  background: string
}
export type ZoneCardSelectorProps = {
  __typename: 'StrapiGQL_ComponentZonesCardSelector'
  title: string
  body: string
  items: CardSelectorColor[]
  detailLinkUrl?: string
  detailLinkTitle?: string
  detailLinkTarget?: string
}
export default function ZoneCardSelector(props: ZoneCardSelectorProps) {
  const {
    title,
    body,
    items,
    detailLinkTitle,
    detailLinkUrl,
    detailLinkTarget,
  } = props
  const [selectedColor, setSelectedColor] = useState<CardSelectorColor>(
    head(items)
  )
  const isDark = (item: CardSelectorColor) => item.background === '#1A1A1A'
  const isBlank = detailLinkTarget === 'new'
  return (
    <Whitespace type="md">
      <Container fluid={true}>
        <div
          className={classNames(styles.bg, {
            [styles.dark]: isDark(selectedColor),
          })}
          style={{ backgroundColor: selectedColor.background }}
        >
          <Whitespace type="lg" hasAnimation={false}>
            <Container>
              <Row>
                <Column auto className={styles.contentCol}>
                  <Heading
                    type="lg"
                    className={styles.title}
                    theme={isDark(selectedColor) && 'dark'}
                  >
                    {title}
                  </Heading>
                  <FreeText
                    type="md"
                    innerHTML={body}
                    color={isDark(selectedColor) ? 'white' : 'primary'}
                  >
                    {body}
                  </FreeText>
                  <FreeText type="sm" color="secondary">
                    What's your color?
                  </FreeText>
                  <div className={styles.selector}>
                    {map(
                      item => (
                        <span
                          onClick={() => setSelectedColor(item)}
                          className={styles.selectorColorItem}
                          key={item.color}
                          style={{ backgroundColor: item.color }}
                        >
                          {item === selectedColor && (
                            <Icon icon={getIconByName('active-tick')} />
                          )}
                        </span>
                      ),
                      items
                    )}
                  </div>
                  {detailLinkTitle && (
                    <FreeText type="m" className={styles.detailLink}>
                      <Permalink
                        to={detailLinkUrl}
                        isExternal={isBlank}
                        isBlank={isBlank}
                      >
                        {detailLinkTitle}
                      </Permalink>
                      <Icon icon={getIconByName('arrow-right')} size={24} />
                    </FreeText>
                  )}
                </Column>
                <Column className={styles.imageCol}>
                  <div className={styles.imagePlacement}>
                    <GatsbyImage
                      image={resolveAsImage(selectedColor.image)}
                      alt={title}
                      loading="lazy"
                      className={styles.image}
                    />

                    <CurvedArrawIcon className={styles.curvedArrowIcon} />

                    <div className={styles.cardMeterialInfo}>
                      <div className={styles.recycleIcon}>
                        <RecycleIcon />
                      </div>

                      <div>It's made from biodegradable materials</div>
                    </div>
                  </div>
                </Column>
              </Row>
            </Container>
          </Whitespace>
        </div>
      </Container>
    </Whitespace>
  )
}
